// modules/SaveSystem.js
// 存档系统模块 - 保存到服务器数据库
import DebugHelper from './DebugHelper.js';
import { getSceneDisplayName, getResumeSceneKey } from './SceneRegistry.js';
import ErrorHandler from './ErrorHandler.js';
import GameStateStore from './GameStateStore.js';

export default class SaveSystem {
    /**
     * 从存档数据中提取 player 对象（兼容新旧格式）
     * @param {Object|null} saveData
     * @returns {Object|null}
     */
    static extractPlayer(saveData) {
        if (!saveData || typeof saveData !== 'object') {
            return null;
        }
        return saveData.player || saveData;
    }

    /**
     * 保存游戏到服务器数据库
     * @param {number} slot - 存档槽位 (0-2) - 保留参数以兼容现有代码，但实际不使用
     * @returns {Promise<boolean>} 是否保存成功
     */
    static async save(slot = 0) {
        try {
            if (!GameStateStore.getState()) {
                ErrorHandler.handleError('游戏状态不存在，无法保存', '存档保存');
                return false;
            }
            
            // 检查是否已登录
            if (!window.userInfo || !window.userInfo.userId) {
                ErrorHandler.handleError('未登录，无法保存游戏', '存档保存');
                return false;
            }
            
            // 获取网络管理器（优先使用全局的，否则使用多人游戏的）
            const networkManager = window.networkManager || window.loginNetworkManager;
            
            if (!networkManager || !networkManager.isConnected()) {
                ErrorHandler.handleError('未连接到服务器，无法保存', '存档保存');
                return false;
            }
            
            const savePayload = GameStateStore.buildSavePayload();
            if (!savePayload) {
                ErrorHandler.handleError('无法构建存档数据', '存档保存');
                return false;
            }

            if (!savePayload.currentScene) {
                DebugHelper.warn('SaveSystem: currentScene 未设置，存档可能缺少场景信息');
            }
            
            // 通过WebSocket发送保存请求
            return new Promise((resolve) => {
                let settled = false;

                const finish = (success) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timeoutId);
                    resolve(success);
                };

                const onSuccess = () => {
                    if (window.userInfo) {
                        window.userInfo.playerData = savePayload;
                    }
                    DebugHelper.log('SaveSystem: 游戏已保存到服务器');
                    finish(true);
                };

                const unsubscribe = networkManager.once('savePlayerDataSuccess', onSuccess);
                
                networkManager.send('savePlayerData', {
                    playerData: savePayload
                });
                
                const timeoutId = setTimeout(() => {
                    unsubscribe();
                    DebugHelper.warn('SaveSystem: 保存超时');
                    finish(false);
                }, 5000);
            });
        } catch (error) {
            ErrorHandler.handleError(error, '存档保存');
            return false;
        }
    }
    
    /**
     * 从服务器数据库加载游戏
     * @param {number} slot - 存档槽位 (0-2) - 保留参数以兼容现有代码，但实际不使用
     * @returns {Promise<Object|null>} 存档数据，失败返回null
     */
    static async load(slot = 0) {
        try {
            // 检查是否已登录
            if (!window.userInfo || !window.userInfo.userId) {
                ErrorHandler.handleError('未登录，无法加载游戏', '存档加载');
                return null;
            }
            
            // 获取网络管理器
            const networkManager = window.networkManager || window.loginNetworkManager;

            if (!networkManager || !networkManager.isConnected()) {
                const cachedPlayerData = window.userInfo?.playerData;
                if (cachedPlayerData && Object.keys(cachedPlayerData).length > 0) {
                    GameStateStore.applySavePayload(cachedPlayerData);
                    DebugHelper.log('SaveSystem: 使用登录缓存的玩家数据');
                    return {
                        gameState: GameStateStore.getState(),
                        timestamp: Date.now(),
                        version: '1.0.1',
                        slot: slot
                    };
                }

                ErrorHandler.handleError('未连接到服务器，无法加载', '存档加载');
                return null;
            }
            
            // 通过WebSocket发送加载请求
            return new Promise((resolve) => {
                let settled = false;

                const finish = (result) => {
                    if (settled) return;
                    settled = true;
                    clearTimeout(timeoutId);
                    resolve(result);
                };

                const onLoaded = (payload) => {
                    const saveData = payload.playerData || {};
                    GameStateStore.applySavePayload(saveData);

                    if (window.userInfo) {
                        window.userInfo.playerData = saveData;
                    }
                    
                    DebugHelper.log('SaveSystem: 从服务器加载游戏成功，场景:', getResumeSceneKey(GameStateStore.getState()));
                    finish({
                        gameState: GameStateStore.getState(),
                        timestamp: Date.now(),
                        version: '1.0.1',
                        slot: slot
                    });
                };

                const unsubscribe = networkManager.once('loadPlayerDataSuccess', onLoaded);
                
                networkManager.send('loadPlayerData', {});
                
                const timeoutId = setTimeout(() => {
                    unsubscribe();
                    DebugHelper.warn('SaveSystem: 加载超时');
                    finish(null);
                }, 5000);
            });
        } catch (error) {
            ErrorHandler.handleError(error, '存档加载');
            return null;
        }
    }
    
    /**
     * 检查是否有存档（从服务器）
     * @param {number} slot - 存档槽位 (0-2) - 保留参数以兼容现有代码
     * @returns {Promise<boolean>} 是否有存档
     */
    static async hasSave(slot = 0) {
        if (!window.userInfo || !window.userInfo.userId) {
            return false;
        }
        
        const player = this.extractPlayer(window.userInfo.playerData);
        if (!player || Object.keys(player).length === 0) {
            return false;
        }

        return !!(player.name && player.element && (player.hp > 0 || player.maxHp > 0 || player.level > 1));
    }
    
    /**
     * 获取存档信息（从服务器）
     * @param {number} slot - 存档槽位 (0-2) - 保留参数以兼容现有代码
     * @returns {Promise<Object|null>} 存档信息 {timestamp, version, playerName, level, location}
     */
    static async getSaveInfo(slot = 0) {
        try {
            if (!window.userInfo || !window.userInfo.userId) {
                return null;
            }
            
            const saveData = window.userInfo.playerData;
            const player = this.extractPlayer(saveData) || GameStateStore.getPlayer();
            
            if (!player) {
                return null;
            }

            const gameState = GameStateStore.getState();
            const resumeScene = getResumeSceneKey(gameState);
            
            return {
                slot: slot,
                timestamp: Date.now(),
                version: saveData?.version || '1.0.1',
                playerName: player.name || '未知',
                level: player.level || 1,
                location: getSceneDisplayName(resumeScene),
                playTime: '已保存'
            };
        } catch (error) {
            DebugHelper.error('SaveSystem: 获取存档信息失败', error);
            return null;
        }
    }
    
    /**
     * 获取所有存档信息（服务器只有一个存档）
     * @returns {Promise<Array>} 存档信息数组
     */
    static async getAllSaveInfo() {
        const info = await this.getSaveInfo(0);
        return info ? [info] : [];
    }
    
    /**
     * 删除存档（服务器不支持删除，只能覆盖）
     * @param {number} slot - 存档槽位 (0-2) - 保留参数以兼容现有代码
     * @returns {Promise<boolean>} 是否删除成功
     */
    static async deleteSave(slot = 0) {
        DebugHelper.log(`SaveSystem: 服务器存档不支持删除，只能覆盖`);
        return false;
    }
    
    /**
     * 获取位置名称
     * @param {Object} gameState - 游戏状态
     * @returns {string} 位置名称
     */
    static getLocationName(gameState) {
        return getSceneDisplayName(getResumeSceneKey(gameState));
    }
    
    /**
     * 计算游戏时长（简单版本）
     * @param {number} timestamp - 存档时间戳
     * @returns {string} 格式化后的时间
     */
    static calculatePlayTime(timestamp) {
        const now = Date.now();
        const diff = now - timestamp;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return `${hours}小时${minutes}分钟前`;
        } else {
            return `${minutes}分钟前`;
        }
    }
    
    /**
     * 格式化存档时间
     * @param {number} timestamp - 时间戳
     * @returns {string} 格式化后的时间字符串
     */
    static formatSaveTime(timestamp) {
        const date = new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }
}
